/**
 * Local chart entries (Phase 16C C2). Encrypted store `chartEntries`, key = entryId.
 *
 * Records keep the API's raw entry shape (entryId, entryType, data, timestamp, version, ...) so
 * the charting service formats local and server entries the same way. Entries read from the server
 * for a visit are cached (origin 'server') so the visit can be charted after the signal drops.
 */
import type { OfflineRuntime } from '../runtime';
import type { SyncState } from './visitRepo';

export interface LocalChartEntry {
  entryId: string;
  visitId: string;
  patientId: string;
  entryType: string;
  data: unknown;
  observedAt?: string;
  documentedAt?: string;
  /** API field: when the entry was written */
  timestamp: string;
  lastEdited?: string;
  provider?: string;
  createdBy?: string;
  status?: string;
  lateEntry?: boolean;
  minutesAfterEnd?: number | null;
  /** Last version the server confirmed (the expectedVersion of the next update) */
  serverVersion?: number;
  /** Bumped on every local edit */
  localVersion: number;
  syncState: SyncState;
  origin: 'local' | 'server';
  /** Other API fields of a cached server entry (amendments, addendum, flags) */
  extra?: Record<string, unknown>;
}

export interface CreateLocalEntryInput {
  entryId: string;
  visitId: string;
  patientId: string;
  entryType: string;
  data: unknown;
  observedAt?: string;
  provider?: string;
  createdBy?: string;
}

export class ChartRepo {
  constructor(private readonly rt: OfflineRuntime) {}

  private stamp(): string { return new Date(this.rt.now()).toISOString(); }

  get(entryId: string): Promise<LocalChartEntry | undefined> {
    return this.rt.store().get<LocalChartEntry>('chartEntries', entryId);
  }

  async put(entry: LocalChartEntry): Promise<LocalChartEntry> {
    await this.rt.store().put('chartEntries', entry.entryId, entry);
    return entry;
  }

  async createLocal(input: CreateLocalEntryInput): Promise<LocalChartEntry> {
    const now = this.stamp();
    return this.put({ ...input, timestamp: now, documentedAt: now, localVersion: 1, syncState: 'pending', origin: 'local' });
  }

  async updateLocal(entryId: string, patch: { entryType?: string; data: unknown; observedAt?: string }): Promise<LocalChartEntry> {
    const entry = await this.get(entryId);
    if (!entry) throw new Error('Entry not found on this device');
    return this.put({
      ...entry,
      ...(patch.entryType ? { entryType: patch.entryType } : {}),
      data: patch.data,
      ...(patch.observedAt ? { observedAt: patch.observedAt } : {}),
      lastEdited: this.stamp(),
      localVersion: entry.localVersion + 1,
      syncState: 'pending',
    });
  }

  async listForVisit(visitId: string): Promise<LocalChartEntry[]> {
    return (await this.rt.store().getAll<LocalChartEntry>('chartEntries'))
      .map((r) => r.value)
      .filter((e) => e.visitId === visitId)
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }

  /** Cache what the server returned for a visit; unsynced local entries win. */
  async cacheServer(visitId: string, patientId: string, raw: Array<Record<string, unknown>>): Promise<void> {
    const local = new Map((await this.listForVisit(visitId)).map((e) => [e.entryId, e]));
    for (const item of raw) {
      const entryId = typeof item.entryId === 'string' ? item.entryId : null;
      if (!entryId) continue;
      const mine = local.get(entryId);
      if (mine && mine.syncState !== 'synced') continue;
      const { entryType, data, timestamp, lastEdited, provider, createdBy, status, lateEntry, minutesAfterEnd, editVersion, observedAt, documentedAt, ...extra } = item;
      await this.put({
        entryId,
        visitId,
        patientId,
        entryType: String(entryType || ''),
        data,
        timestamp: typeof timestamp === 'string' ? timestamp : this.stamp(),
        lastEdited: typeof lastEdited === 'string' ? lastEdited : undefined,
        provider: typeof provider === 'string' ? provider : undefined,
        createdBy: typeof createdBy === 'string' ? createdBy : undefined,
        status: typeof status === 'string' ? status : undefined,
        lateEntry: lateEntry === true,
        minutesAfterEnd: typeof minutesAfterEnd === 'number' ? minutesAfterEnd : null,
        observedAt: typeof observedAt === 'string' ? observedAt : undefined,
        documentedAt: typeof documentedAt === 'string' ? documentedAt : undefined,
        // 16B editVersion is the optimistic-concurrency counter (version stays in `extra` for the UI)
        serverVersion: typeof editVersion === 'number' ? editVersion : undefined,
        localVersion: 1,
        syncState: 'synced',
        origin: mine?.origin || 'server',
        extra,
      });
    }
  }

  async markSynced(entryId: string, version?: number, patch: Partial<LocalChartEntry> = {}): Promise<void> {
    const entry = await this.get(entryId);
    if (entry) await this.put({ ...entry, ...patch, serverVersion: version ?? entry.serverVersion ?? 1, syncState: 'synced' });
  }

  async setSyncState(entryId: string, syncState: SyncState): Promise<void> {
    const entry = await this.get(entryId);
    if (entry) await this.put({ ...entry, syncState });
  }

  /** Back to the raw API shape (plus syncState) for the charting service's formatter. */
  static toApiShape(e: LocalChartEntry): Record<string, unknown> {
    return {
      ...(e.extra || {}),
      entryId: e.entryId,
      entryType: e.entryType,
      data: e.data,
      timestamp: e.timestamp,
      lastEdited: e.lastEdited,
      provider: e.provider,
      createdBy: e.createdBy,
      status: e.status,
      lateEntry: e.lateEntry,
      minutesAfterEnd: e.minutesAfterEnd,
      observedAt: e.observedAt,
      documentedAt: e.documentedAt,
      ...(e.serverVersion !== undefined ? { editVersion: e.serverVersion } : {}),
      syncState: e.syncState,
    };
  }

  async delete(entryId: string): Promise<void> {
    await this.rt.store().delete('chartEntries', entryId);
  }
}
